/**
 * CONTRACT (Wave 0 · T0.5) — FROZEN.
 * Canonical environment-variable NAMES for both the FE app and the BFF.
 *
 * Freezing the names here lets scaffolds (.env.example) and the wiring task
 * (T4.3 .env.development) agree without editing each other's files. No secrets
 * or environment-specific values live here — names only (per the repo
 * CLAUDE.md security rules).
 */

/** Frontend (Vite) env var names. Injected at runtime via `window.__ENV__`. */
export const FE_ENV = {
  /** Base URL of the onboarding BFF. */
  BFF_URL: 'VITE_ONBOARDING_BFF_URL',
  /** Where the Preparing screen redirects on `ready` (Gap G3 placeholder). */
  BACKOFFICE_LOGIN_URL: 'VITE_BACKOFFICE_LOGIN_URL',
  /** Dev server port. */
  PORT: 'VITE_ONBOARDING_PORT',
} as const;

/** BFF (Fastify) env var names. */
export const BFF_ENV = {
  USERS_API_URL: 'ONBOARDING_USERS_API_URL',
  AUTH_API_URL: 'ONBOARDING_AUTH_API_URL',
  TENANCY_API_URL: 'ONBOARDING_TENANCY_API_URL',
  /** Managed platform JWT / API key — injected server-side ONLY. */
  TSS_API_KEY: 'TSS_API_KEY',
  /**
   * AMENDMENT (Cognito service-user auth + caller gate): the same pool/client
   * names are used to (1) verify operator ID tokens on provision and
   * (2) mint a service-user ID token (group `tss-admin`) for tenancy + auth.
   * Credentials for (2) are injected server-side ONLY (from Secrets Manager).
   */
  COGNITO_REGION: 'ONBOARDING_COGNITO_REGION',
  COGNITO_USER_POOL_ID: 'ONBOARDING_COGNITO_USER_POOL_ID',
  COGNITO_APP_CLIENT_ID: 'ONBOARDING_COGNITO_APP_CLIENT_ID',
  COGNITO_SVC_USERNAME: 'ONBOARDING_COGNITO_SVC_USERNAME',
  COGNITO_SVC_PASSWORD: 'ONBOARDING_COGNITO_SVC_PASSWORD',
  /** `mock` (default) | `connect`. */
  TENANCY_MODE: 'TENANCY_MODE',
  /**
   * AMENDMENT (auth-provisioning unmock): `mock` (default) | `connect`.
   * Selects the AuthProvisionPort + user-registration implementation,
   * INDEPENDENT of TENANCY_MODE (tenancy stays mock).
   */
  AUTH_PROVISION_MODE: 'AUTH_PROVISION_MODE',
  /** Redirect target handed back to the FE. */
  BACKOFFICE_LOGIN_URL: 'BACKOFFICE_LOGIN_URL',
  /** Listen port. */
  PORT: 'PORT',
  /**
   * AMENDMENT (WS-D): account-routing env selector threaded to tenancy
   * `RegisterStore` (e.g. `dev`, `staging`, `prod`).
   */
  ENV: 'BFF_ENV_NAME',
  /** `memory` (default, tests + local) | `redis` (multi-instance prod). */
  PROVISION_STORE_BACKEND: 'PROVISION_STORE_BACKEND',
  /** Redis URL when PROVISION_STORE_BACKEND=redis. */
  REDIS_URL: 'REDIS_URL',
  /** Provision-record TTL (seconds) when backend = redis. Default 86400 (24h). */
  PROVISION_TTL_SECONDS: 'PROVISION_TTL_SECONDS',
  ONBOARDING_PROVISION_WAIT_TIMEOUT_MS: 'ONBOARDING_PROVISION_WAIT_TIMEOUT_MS',
  /** Per-upstream wall-clock (tenancy / auth / users) in ms. Default 5000. */
  UPSTREAM_TIMEOUT_MS: 'UPSTREAM_TIMEOUT_MS',
  /**
   * Magic-link auto sign-in (WS-G2). `true` enables; default OFF until the
   * backoffice exchange page lands (G2.4 + G2.5).
   */
  BFF_AUTO_SIGN_IN: 'BFF_AUTO_SIGN_IN',
  /** TTL (seconds) for the handoff JWT minted in finalise(). Default 60. */
  BFF_HANDOFF_TTL_SECONDS: 'BFF_HANDOFF_TTL_SECONDS',
  /**
   * AMENDMENT (onboarding phase 1, chat foundation): per-service adapter mode,
   * `mock` (default) | `connect`. Each upstream (competitive-intelligence,
   * social-scraping, funnel-analysis, site-profile) runs through its real adapter;
   * `mock` only swaps the HTTP transport for JSON fixtures. Any `connect` mode
   * requires ONBOARDING_SESSION_STORE_BACKEND=redis (checked at boot), unless the single-operator override
   * ONBOARDING_ALLOW_MEMORY_STORE_WITH_CONNECT=true is set: the boot then logs one warning and runs on the
   * per-process memory store (one instance only; a restart loses every session).
   */
  CI_MODE: 'CI_MODE',
  SOCIAL_SCRAPING_MODE: 'SOCIAL_SCRAPING_MODE',
  FUNNEL_ANALYSIS_MODE: 'FUNNEL_ANALYSIS_MODE',
  SITE_PROFILE_MODE: 'SITE_PROFILE_MODE',
  /** AMENDMENT (phase 1): upstream base URLs, used only when the service mode is `connect`. */
  CI_BASE_URL: 'CI_BASE_URL',
  SOCIAL_SCRAPING_BASE_URL: 'SOCIAL_SCRAPING_BASE_URL',
  FUNNEL_ANALYSIS_BASE_URL: 'FUNNEL_ANALYSIS_BASE_URL',
  /** AMENDMENT (phase 1): per-service wall-clock budget in ms (a start call costs real money upstream). */
  CI_TIMEOUT_MS: 'CI_TIMEOUT_MS',
  SOCIAL_SCRAPING_TIMEOUT_MS: 'SOCIAL_SCRAPING_TIMEOUT_MS',
  FUNNEL_ANALYSIS_TIMEOUT_MS: 'FUNNEL_ANALYSIS_TIMEOUT_MS',
  SITE_PROFILE_TIMEOUT_MS: 'SITE_PROFILE_TIMEOUT_MS',
  /**
   * AMENDMENT (phase 1): mock-fixture knobs. `MOCK_TIME_SCALE` multiplies the
   * simulated clock of sequence fixtures (1 = recorded pace, 10 = ten times faster).
   * `MOCK_SCENARIO` picks the `<op>.<scenario>.json` fixture variant.
   */
  MOCK_TIME_SCALE: 'MOCK_TIME_SCALE',
  MOCK_SCENARIO: 'MOCK_SCENARIO',
  /** AMENDMENT (phase 1): onboarding-session record TTL (seconds). Default 86400 (24h). */
  ONBOARDING_SESSION_TTL_SECONDS: 'ONBOARDING_SESSION_TTL_SECONDS',
  /** AMENDMENT (phase 1): `memory` (default) | `redis`. Redis URL is shared (REDIS_URL). */
  ONBOARDING_SESSION_STORE_BACKEND: 'ONBOARDING_SESSION_STORE_BACKEND',
  /**
   * AMENDMENT (phase 1, tuning knobs): previously code-level defaults in the BFF
   * `CONFIG_DEFAULTS`. Every value is a positive number; an unset, zero, negative or
   * non-numeric value falls back to the default (the integer ones also reject fractions).
   * `MOCK_MAX_CLOCKS`: simulated fixture clocks kept before the oldest is evicted (default 1000).
   * `RESEARCH_HTTP_MAX_BODY_BYTES`: response cap of the HTTP transport shared by CI /
   * social-scraping / funnel-analysis (default 5000000).
   */
  MOCK_MAX_CLOCKS: 'MOCK_MAX_CLOCKS',
  RESEARCH_HTTP_MAX_BODY_BYTES: 'RESEARCH_HTTP_MAX_BODY_BYTES',
  /**
   * AMENDMENT (phase 2): response cap (bytes) of the CI transport only. Defaults to the effective
   * `RESEARCH_HTTP_MAX_BODY_BYTES`. CI `GET /workflows/:id/artifacts` returns EVERY artifact of the
   * 12-job run in one body; measure it against the first real capture before `CI_MODE=connect`.
   */
  CI_MAX_BODY_BYTES: 'CI_MAX_BODY_BYTES',
  /**
   * AMENDMENT (phase 1, tuning knobs): site-profile fetch limits. Body cap per page in
   * bytes (default 3000000), redirects followed by hand (default 3), and the budget for
   * resolving one hostname in ms (default 3000).
   */
  SITE_PROFILE_MAX_BYTES: 'SITE_PROFILE_MAX_BYTES',
  SITE_PROFILE_MAX_REDIRECTS: 'SITE_PROFILE_MAX_REDIRECTS',
  SITE_PROFILE_DNS_TIMEOUT_MS: 'SITE_PROFILE_DNS_TIMEOUT_MS',
  /**
   * AMENDMENT (phase 1, tuning knobs): onboarding-session store behaviour. Least ms between
   * two upstream status refreshes of one job (default 1500); how long a job claimed but
   * never given an upstream ref waits before it is marked unavailable (default 60000, keep
   * it above every upstream timeout); sessions whose derived view one process keeps, oldest
   * evicted (default 1000).
   */
  ONBOARDING_SESSION_REFRESH_MIN_INTERVAL_MS: 'ONBOARDING_SESSION_REFRESH_MIN_INTERVAL_MS',
  ONBOARDING_SESSION_START_STALE_MS: 'ONBOARDING_SESSION_START_STALE_MS',
  ONBOARDING_SESSION_CACHE_MAX_ENTRIES: 'ONBOARDING_SESSION_CACHE_MAX_ENTRIES',
  /**
   * AMENDMENT (phase 1, hardening): site-profile parse cap in bytes (default 524288) and
   * the most site reads running at once in this process (default 8).
   */
  SITE_PROFILE_MAX_PARSE_BYTES: 'SITE_PROFILE_MAX_PARSE_BYTES',
  SITE_PROFILE_MAX_CONCURRENT: 'SITE_PROFILE_MAX_CONCURRENT',
  /**
   * AMENDMENT (phase 1, hardening): per-operator session limits. NEW sessions one operator
   * may create per window (default 20 per 3600 s) and live sessions one operator may hold
   * (default 50). Over either gives 429 `rate_limited` with `Retry-After`. Attaching to
   * an existing session is never counted.
   */
  ONBOARDING_SESSION_CREATE_MAX_PER_WINDOW: 'ONBOARDING_SESSION_CREATE_MAX_PER_WINDOW',
  ONBOARDING_SESSION_CREATE_WINDOW_SECONDS: 'ONBOARDING_SESSION_CREATE_WINDOW_SECONDS',
  ONBOARDING_SESSION_MAX_LIVE_PER_OPERATOR: 'ONBOARDING_SESSION_MAX_LIVE_PER_OPERATOR',
  /**
   * AMENDMENT (phase 1, e2e): `true` makes the BFF accept ONE fixed e2e operator token as a
   * platform admin (Playwright runs only; owner-approved). Honoured ONLY when NODE_ENV=test:
   * the BFF refuses to boot if this is set under any other NODE_ENV. Never set it in a
   * deployed environment.
   */
  E2E_AUTH_BYPASS: 'ONBOARDING_E2E_AUTH_BYPASS',
  /**
   * AMENDMENT (provisioning kill switch): tenant / infra provisioning (POST /provision, its
   * status poll and SSE stream, the tenancy `RegisterTenant` / `RegisterStore` calls and the
   * auth user registration) runs ONLY when this is exactly `true`. Any other value, or unset,
   * keeps it OFF: the provision routes answer 503 `provisioning_disabled` and no tenancy or
   * auth-provision adapter is built. Default OFF; the Dockerfile never sets it.
   */
  ONBOARDING_PROVISIONING_ENABLED: 'ONBOARDING_PROVISIONING_ENABLED',
  /**
   * AMENDMENT (onboarding phase 2, memory store with connect): `true` lets a `connect` research
   * mode run on the per-process memory session store; the boot guard then logs ONE warning
   * instead of refusing. Default OFF. Restarts lose every session and can re-run paid CI work
   * and scrapes; only a single instance is safe. Phase 2.a (redis) retires it.
   */
  ONBOARDING_ALLOW_MEMORY_STORE_WITH_CONNECT: 'ONBOARDING_ALLOW_MEMORY_STORE_WITH_CONNECT',
  /**
   * AMENDMENT (onboarding phase 2, upstream identity): the BFF exchanges a tss-svc-auth service
   * API key (`TSS_SERVICE_API_KEY`) at `POST {TSS_AUTH_BASE_URL}/api/v1/auth/token` for a token
   * scoped to the reserved onboarding tenant and store (`ONBOARDING_PROSPECT_TENANT_ID` /
   * `ONBOARDING_PROSPECT_STORE_ID`, UUIDs). The key is a SECRET: injected from Secrets Manager
   * into the task env, never a literal, never logged. All four are required when any upstream
   * that needs identity (CI, social-scraping, funnel-analysis, marketing) runs in `connect`;
   * `mock` never reads them. Under NODE_ENV=production `TSS_AUTH_BASE_URL` must be https.
   * Token tuning (positive ms; invalid values fall back to the default):
   * `TSS_SERVICE_TOKEN_REFRESH_SKEW_MS` (60000): how long before expiry a cached token is refreshed;
   * `TSS_SERVICE_TOKEN_TIMEOUT_MS` (5000): budget of one token exchange (it is on top of the
   * upstream's own timeout, so start-stale must exceed both);
   * `TSS_SERVICE_TOKEN_FAILURE_BACKOFF_MS` (5000): after a failed exchange the scope is not
   * retried for this long (calls fail fast without a network call);
   * `TSS_SERVICE_TOKEN_AUTH_FAILURE_BACKOFF_MS` (30000): the same after a 401/403 (a rejected key).
   */
  TSS_AUTH_BASE_URL: 'TSS_AUTH_BASE_URL',
  TSS_SERVICE_API_KEY: 'TSS_SERVICE_API_KEY',
  ONBOARDING_PROSPECT_TENANT_ID: 'ONBOARDING_PROSPECT_TENANT_ID',
  ONBOARDING_PROSPECT_STORE_ID: 'ONBOARDING_PROSPECT_STORE_ID',
  TSS_SERVICE_TOKEN_REFRESH_SKEW_MS: 'TSS_SERVICE_TOKEN_REFRESH_SKEW_MS',
  TSS_SERVICE_TOKEN_TIMEOUT_MS: 'TSS_SERVICE_TOKEN_TIMEOUT_MS',
  TSS_SERVICE_TOKEN_FAILURE_BACKOFF_MS: 'TSS_SERVICE_TOKEN_FAILURE_BACKOFF_MS',
  TSS_SERVICE_TOKEN_AUTH_FAILURE_BACKOFF_MS: 'TSS_SERVICE_TOKEN_AUTH_FAILURE_BACKOFF_MS',
  /**
   * AMENDMENT (onboarding phase 2, marketing): marketing-core adapter mode (`mock` default |
   * `connect`), its base URL (required when `connect`) and its per-call budget in ms (default 10000).
   */
  MARKETING_MODE: 'MARKETING_MODE',
  MARKETING_BASE_URL: 'MARKETING_BASE_URL',
  MARKETING_TIMEOUT_MS: 'MARKETING_TIMEOUT_MS',
  /**
   * AMENDMENT (onboarding phase 2, ad analysis): knobs of the `adAnalysis:{platform}` job. Positive numbers;
   * an unset, zero, negative or non-numeric value (a fraction too, for the integer one) falls back to the default.
   * `AD_ANALYSIS_STALL_MS` (120000): analyzed-ad coverage that has not grown for this long is stalled; the
   * stall fires ONE fallback `ad-analysis/trigger` for the job's current account (bounded per session by
   * `AD_ANALYSIS_MAX_TRIGGERS_PER_SESSION`, and across sessions by a global per-account claim, see below), and a
   * second stall of that account ends the job as `partial`. `AD_ANALYSIS_SETTLE_MS` (300000): from the start of the job, a profile that is still
   * `insufficient_data` or `partial` is written as `partial` (it can still become `done` when the profile turns
   * `ready` later); must exceed the stall window (checked at boot). `AD_ANALYSIS_TRIGGER_LIMIT_MAX` (25):
   * the most ads one fallback trigger may ask marketing-core to analyze (each static ad costs an LLM call).
   * `AD_ANALYSIS_REFRESH_MIN_INTERVAL_MS` (5000): least time between two profile reads of one job; the profile
   * is a corpus query, heavier than the other status reads. `AD_ANALYSIS_POLL_HORIZON_MS` (1800000, 30 min):
   * a `partial` job is polled (so a late `ready` can still make it `done`) until this long after its start, then
   * it is settled for good; must exceed the settle deadline (checked at boot).
   * Cost bound of the paid fallback trigger: `AD_ANALYSIS_TRIGGER_ACCOUNT_TTL_MS` (86400000, 24 h): a trigger for
   * an account is sent at most once per this long ACROSS ALL SESSIONS (a global claim outside the session);
   * `AD_ANALYSIS_MAX_TRIGGERS_PER_SESSION` (2): a session sends at most this many fallback triggers, whatever
   * accounts the confirmations move its job through. (A per-operator budget is a follow-up.)
   */
  AD_ANALYSIS_STALL_MS: 'AD_ANALYSIS_STALL_MS',
  AD_ANALYSIS_SETTLE_MS: 'AD_ANALYSIS_SETTLE_MS',
  AD_ANALYSIS_TRIGGER_LIMIT_MAX: 'AD_ANALYSIS_TRIGGER_LIMIT_MAX',
  AD_ANALYSIS_REFRESH_MIN_INTERVAL_MS: 'AD_ANALYSIS_REFRESH_MIN_INTERVAL_MS',
  AD_ANALYSIS_POLL_HORIZON_MS: 'AD_ANALYSIS_POLL_HORIZON_MS',
  AD_ANALYSIS_TRIGGER_ACCOUNT_TTL_MS: 'AD_ANALYSIS_TRIGGER_ACCOUNT_TTL_MS',
  AD_ANALYSIS_MAX_TRIGGERS_PER_SESSION: 'AD_ANALYSIS_MAX_TRIGGERS_PER_SESSION',
} as const;

export type TenancyMode = 'mock' | 'connect';
export const TENANCY_MODES: TenancyMode[] = ['mock', 'connect'];
export const DEFAULT_TENANCY_MODE: TenancyMode = 'mock';

export type AuthProvisionMode = 'mock' | 'connect';
export const AUTH_PROVISION_MODES: AuthProvisionMode[] = ['mock', 'connect'];
export const DEFAULT_AUTH_PROVISION_MODE: AuthProvisionMode = 'mock';

/** AMENDMENT (phase 1): adapter mode shared by the research services (marketing joined in phase 2). */
export type ServiceMode = 'mock' | 'connect';
export const SERVICE_MODES: ServiceMode[] = ['mock', 'connect'];
export const DEFAULT_SERVICE_MODE: ServiceMode = 'mock';

/** AMENDMENT (phase 1): which fixture variant `mock` serves (`<op>.<scenario>.json`, else the default). */
export type MockScenario = 'ok' | 'partial' | 'unavailable' | 'empty';
export const MOCK_SCENARIOS: MockScenario[] = ['ok', 'partial', 'unavailable', 'empty'];
export const DEFAULT_MOCK_SCENARIO: MockScenario = 'ok';

/** AMENDMENT (phase 1): onboarding-session store backend. */
export type SessionStoreBackend = 'memory' | 'redis';
export const SESSION_STORE_BACKENDS: SessionStoreBackend[] = ['memory', 'redis'];
export const DEFAULT_SESSION_STORE_BACKEND: SessionStoreBackend = 'memory';
